/**
 * Core units CRUD operations
 */

import { supabase } from '../supabase'
import type { Unit, UserUnit, Role } from '../types'
import { getAuthUser, PROFILES_USER_ID_COL } from './helpers'
import { updateUnitContext as updateUnitContextRPC, saveEnabledGroups } from '../contexts'
import { validateCapability, validateOwner } from '../services/permissions'

/**
 * Get all units the user has access to
 * Prefers RPC get_user_units, falls back to a simple query.
 */
export async function getUserUnits(userId: string): Promise<UserUnit[]> {
  // 1) Try RPC
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('get_user_units', { p_user_id: userId })

  if (!rpcError && Array.isArray(rpcData)) {
    return rpcData
  }

  if (rpcError) {
    console.warn('[getUserUnits] RPC failed, using fallback:', rpcError)
  }

  // 2) Fallback: units created_by OR units where user is member
  const { data, error } = await supabase
    .from('units')
    .select(`
      *,
      unit_members!left (
        user_id,
        role
      )
    `)
    .or(`created_by.eq.${userId},unit_members.user_id.eq.${userId}`)

  if (error) throw error

  // normalize to UserUnit shape
  const result: UserUnit[] = (data || []).map((u: any) => {
    const membership = (u.unit_members || []).find((m: any) => m.user_id === userId)
    return {
      id: u.id,
      name: u.name,
      slug: u.slug,
      role: membership?.role ?? (u.created_by === userId ? 'admin' : 'viewer'),
      created_at: u.created_at,
      updated_at: u.updated_at,
      pix_key: u.pix_key || null
    } as any
  })

  return result
}

/**
 * Get units the user has access to filtered by context key.
 * Returns an empty array if the user has no units for that context.
 */
export async function getUserUnitsByContext(userId: string, contextKey: string): Promise<UserUnit[]> {
  // Step 1: resolve context_id from the key
  const { data: contextData, error: contextError } = await supabase
    .from('contexts')
    .select('id')
    .eq('key', contextKey)
    .maybeSingle()

  if (contextError) {
    console.warn('[getUserUnitsByContext] Error fetching context:', contextError)
    return []
  }

  if (!contextData) {
    console.warn('[getUserUnitsByContext] No context found for key:', contextKey)
    // Fallback: try filtering by context_key column on units table directly
    const { data: fallback } = await supabase
      .from('units')
      .select('*')
      .eq('context_key', contextKey)
      .eq('created_by', userId)
    console.log('[getUserUnitsByContext] Fallback result:', fallback?.length ?? 0)
    return (fallback || []).map((u: any) => ({ ...u, role: 'admin' }) as any)
  }

  const contextId = contextData.id
  console.log('[getUserUnitsByContext] Resolved context_id:', contextId, 'for key:', contextKey)

  // Step 2a: units created by user in this context
  const { data: createdUnits, error: createdError } = await supabase
    .from('units')
    .select('*')
    .eq('context_id', contextId)
    .eq('created_by', userId)

  if (createdError) {
    console.warn('[getUserUnitsByContext] Error fetching created units:', createdError)
  }

  // Step 2b: units where user is a member in this context
  const { data: memberRows, error: memberError } = await supabase
    .from('unit_members')
    .select('unit_id, role, units!inner(*, context_id)')
    .eq('user_id', userId)
    .eq('units.context_id', contextId)

  if (memberError) {
    console.warn('[getUserUnitsByContext] Error fetching member units:', memberError)
  }

  // Merge, deduplicate by id
  const seen = new Set<string>()
  const result: UserUnit[] = []

  for (const u of (createdUnits || [])) {
    if (!seen.has(u.id)) {
      seen.add(u.id)
      result.push({ ...u, role: 'admin' } as any)
    }
  }
  for (const row of (memberRows || [])) {
    const u = (row as any).units
    if (u && !seen.has(u.id)) {
      seen.add(u.id)
      result.push({ ...u, role: row.role } as any)
    }
  }

  console.log('[getUserUnitsByContext] Found units for context', contextKey, ':', result.length)
  return result
}

/**
 * Get a specific unit by ID with context and enabled groups
 */
export async function getUnit(unitId: string): Promise<Unit & { context_key?: string, enabled_group_ids?: string[] }> {
  // Get unit data
  const { data: unitData, error: unitError } = await supabase
    .from('units')
    .select('*')
    .eq('id', unitId)
    .single()

  if (unitError) throw unitError
  
  const unit = unitData as Unit
  const result: any = { ...unit }

  // Get context_key if context_id exists
  if (unit.context_id) {
    const { data: contextData } = await supabase
      .from('contexts')
      .select('key')
      .eq('id', unit.context_id)
      .single()
    
    if (contextData) {
      result.context_key = contextData.key
    }
  }

  // Get enabled group IDs
  const { data: enabledGroups } = await supabase
    .from('unit_asset_groups')
    .select('group_id')
    .eq('unit_id', unitId)
    .eq('active', true)

  if (enabledGroups && enabledGroups.length > 0) {
    result.enabled_group_ids = enabledGroups.map((g: any) => g.group_id)
  }

  return result
}

/**
 * Create a new unit (simplified - no organization)
 * IMPORTANT: also creates unit_members row for creator as admin.
 */
export async function createUnit(
  name: string,
  slug: string,
  options?: {
    description?: string
    address?: string
    address_number?: string
    city?: string
    state?: string
    zipcode?: string
    area_hectares?: number
    cnpj?: string
    cpf?: string
    legal_name?: string
    trade_name?: string
    state_registration?: string
    municipal_registration?: string
    latitude?: number
    longitude?: number
    context_key?: string
    enabled_group_ids?: string[]
    pix_key?: string
    margin_percentage?: number
  }
): Promise<Unit> {
  const user = await getAuthUser()
  if (!user) throw new Error('User not authenticated')

  // Extract context, groups and margin from options
  const { context_key, enabled_group_ids, margin_percentage, ...unitOptions } = options || {}

  // Prepare attrs merging any provided attrs with margin_percentage
  const providedAttrs = (unitOptions as any).attrs || {}
  const attrs = {
    ...providedAttrs,
    ...(margin_percentage !== undefined ? { margin_percentage } : {})
  }

  // Clean unitOptions to avoid duplicating attrs when spreading
  const { attrs: _dropAttrs, ...unitOptionsWithoutAttrs } = unitOptions as any

  // Create unit
  const { data: unit, error: unitError } = await supabase
    .from('units')
    .insert({
      name,
      slug,
      ...unitOptionsWithoutAttrs,
      attrs,
      created_by: user.id,
      owner_user_id: user.id
    })
    .select()
    .single()

  if (unitError) throw unitError

  // Create membership for creator (admin)
  const { error: memberError } = await supabase
    .from('unit_members')
    .insert({
      unit_id: unit.id,
      user_id: user.id,
      role: 'admin'
    })

  // If membership already exists, ignore conflict-like errors
  if (memberError) {
    console.warn('[createUnit] Could not create admin membership:', memberError)
  }

  // Update context if provided
  if (context_key) {
    try {
      console.log('[createUnit] Updating unit context to:', context_key)
      await updateUnitContextRPC(unit.id, context_key)
      console.log('[createUnit] Context updated successfully')
    } catch (error) {
      console.error('[createUnit] Error updating context:', error)
      throw error
    }
  }

  // Save enabled groups if provided
  if (enabled_group_ids && enabled_group_ids.length > 0) {
    try {
      console.log('[createUnit] Saving enabled groups:', enabled_group_ids)
      await saveEnabledGroups(unit.id, enabled_group_ids)
    } catch (error) {
      console.error('[createUnit] Error saving enabled groups:', error)
      console.error('[createUnit] Error details:', JSON.stringify(error, null, 2))
      // Don't throw - this is not critical for unit creation
    }
  }

  return unit as Unit
}

/**
 * Update a unit
 */
export async function updateUnit(
  unitId: string,
  updates: Partial<Omit<Unit, 'id' | 'created_by' | 'created_at' | 'updated_at'>> & {
    context_key?: string
    enabled_group_ids?: string[]
  },
  userRole?: Role
): Promise<Unit> {
  if (userRole) {
    await validateCapability(userRole, 'canManageSettings')
  }
  // Extract context, groups and margin from updates
  const { context_key, enabled_group_ids, margin_percentage, ...unitUpdates } = updates

  // If margin_percentage is present in updates, merge into attrs
  const updatesWithAttrs = { ...(unitUpdates as any) }
  const providedAttrsUpdate = (unitUpdates as any).attrs || {}
  if (margin_percentage !== undefined) {
    updatesWithAttrs.attrs = {
      ...providedAttrsUpdate,
      margin_percentage
    }
  }

  const { data, error } = await supabase
    .from('units')
    .update(updatesWithAttrs)
    .eq('id', unitId)
    .select()
    .single()

  if (error) throw error

  // Update context if provided
  if (context_key) {
    try {
      await updateUnitContextRPC(unitId, context_key as string)
    } catch (error) {
      console.error('[updateUnit] Error updating context:', error)
      throw error
    }
  }

  // Save enabled groups if provided
  if (enabled_group_ids !== undefined) {
    try {
      await saveEnabledGroups(unitId, enabled_group_ids)
    } catch (error) {
      console.error('[updateUnit] Error saving enabled groups:', error)
      throw error
    }
  }

  return data as Unit
}

/**
 * Delete a unit
 * Requires: User must be owner + have canDeleteUnit capability
 */
export async function deleteUnit(unitId: string, userRole?: Role, isOwner?: boolean): Promise<void> {
  if (userRole || isOwner !== undefined) {
    // If owner check is provided, validate it
    if (isOwner !== undefined) {
      await validateOwner(isOwner)
    }
    // Also validate capability
    if (userRole) {
      await validateCapability(userRole, 'canDeleteUnit')
    }
  }
  const { error } = await supabase
    .from('units')
    .delete()
    .eq('id', unitId)

  if (error) throw error
}

/**
 * Set current unit for user
 * NOTE: your profiles table must have { user_id, current_unit_id, last_unit_id }.
 */
export async function setCurrentUnit(userId: string, unitId: string): Promise<void> {
  console.log('[setCurrentUnit] Updating profile:', { userId, unitId, column: PROFILES_USER_ID_COL })
  
  // First, check if profile exists
  const { data: existingProfile } = await supabase
    .from('profiles')
    .select('user_id, id')
    .eq(PROFILES_USER_ID_COL, userId)
    .maybeSingle()
  
  console.log('[setCurrentUnit] Existing profile:', existingProfile)
  
  if (!existingProfile) {
    // Profile doesn't exist, create it
    console.log('[setCurrentUnit] Profile not found, creating new profile')
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        user_id: userId,
        current_unit_id: unitId,
        last_unit_id: unitId
      })
      .select()
    
    console.log('[setCurrentUnit] Insert result:', { newProfile, insertError })
    
    if (insertError) throw insertError
    return
  }
  
  // Profile exists, update it
  const { data, error } = await supabase
    .from('profiles')
    .update({
      current_unit_id: unitId,
      last_unit_id: unitId
    })
    .eq(PROFILES_USER_ID_COL, userId)
    .select()

  console.log('[setCurrentUnit] Update result:', { data, error })
  
  if (error) throw error
}

/**
 * Get user's current unit
 */
export async function getCurrentUnit(userId: string): Promise<Unit | null> {
  console.log('[getCurrentUnit] Fetching profile for userId:', userId, 'using column:', PROFILES_USER_ID_COL)
  
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('current_unit_id')
    .eq(PROFILES_USER_ID_COL, userId)
    .maybeSingle()

  console.log('[getCurrentUnit] Profile result:', { profile, profileError })

  if (profileError) {
    console.error('[getCurrentUnit] Profile error:', profileError)
    return null
  }
  
  if (!profile?.current_unit_id) {
    console.log('[getCurrentUnit] No current_unit_id in profile')
    return null
  }

  console.log('[getCurrentUnit] Fetching unit with id:', profile.current_unit_id)
  
  const { data: unit, error: unitError } = await supabase
    .from('units')
    .select('*')
    .eq('id', profile.current_unit_id)
    .maybeSingle()

  console.log('[getCurrentUnit] Unit result:', { unit, unitError })

  if (unitError || !unit) return null
  return unit as Unit
}
